import type {
  AdminContactMessage,
  AdminDonationDetail,
  AdminDonationFilters,
  AdminDonationListItem,
  AdminDonationListParams,
  AdminDonationCategoriesResponse,
  AdminDonationSourcesResponse,
  AdminAuditLogListParams,
  AdminDonationsSummary,
  AdminPaginatedAuditLogResponse,
  AdminPaginatedDonationsResponse,
  AdminDonorDetail,
  AdminDonorListItem,
  AdminEvent,
  AdminLoginPayload,
  AdminMonthlyTrendResponse,
  AdminNewsletterSubscriber,
  AdminSession,
  AdminSettings,
  AdminStats,
  AdminUser,
  AdminUserListItem,
  AuditLogItem,
  ContactUpdatePayload,
  CreateAdminEventPayload,
  CreateAdminTestimonialPayload,
  CreateAdminUserPayload,
  DonationReconcilePayload,
  DonationSourceKey,
  FinanceQueueSummary,
  GatewayPaymentStatus,
  MarkPaidFromGatewayPayload,
  ResetReceiptSequencePayload,
  UpdateAdminEventPayload,
  UpdateAdminAboutContentPayload,
  UpdateAdminHomeContentPayload,
  UpdateAdminLegalContentPayload,
  UpdateAdminProgramsContentPayload,
  UpdateAdminImpactContentPayload,
  UpdateAdminTestimonialPayload,
  UpdateAdminSettingsPayload,
  UpdateAdminUserPayload,
  UpdateCmsMediaPayload,
  CmsMediaItem,
  CmsRevision,
  AdminProgramsContent,
  AdminImpactContent,
} from "../admin-api-types";
import type {
  AdminAboutContent,
  AdminHomeContent,
  AdminLegalContent,
  AdminTestimonial,
} from "../admin-api-types";
import type {
  AboutContent,
  ImpactContent,
  ProgramsContent,
  AdminLoginContent,
  ContactPayload,
  CreateOrderPayload,
  CreateOrderResponse,
  DonationStatus,
  Event,
  HomeContent,
  LegalContent,
  NewsletterPayload,
  NewsletterUnsubscribePayload,
  RecentDonor,
  Stats,
  Testimonial,
  VerifyPaymentPayload,
  VerifyPaymentResponse,
  SendOtpPayload,
  VerifyOtpPayload,
  VerifyOtpResponse,
  SendEmailCustomPayload,
} from "../api-types";
import { DONATION_CATEGORIES } from "../donation-categories";
import { getAdminSession } from "../admin-session";
import {
  DONATION_SOURCE_KEYS,
  DONATION_SOURCE_LABELS,
  formatMonthKey,
  formatMonthLabel,
  formatPeriodLabel,
} from "../admin/donation-sources";
import { computeAdminStatsTrends, computeAdminStatsWindowCounts } from "../admin/compute-stats-trends";
import { buildReceiptDocumentData } from "../document-data";
import { donationsToCsv } from "../admin-export-csv";
import type { DocumentKind } from "../document-types";
import { formatEventDateFromIso } from "../format-event-date";
import { assets } from "@/content/assets";
import { adminLoginContent } from "@/content/admin-login";
import { homeTestimonials } from "@/content/home";
import { site } from "@/content/site";
import { siteDefaults } from "@/lib/site-config";
import { adminEventToPublicEvent, isEventPublished } from "@/lib/events-map";
import {
  getMockEventsArray,
  isMockEventsSeeded,
  setMockEventsSeeded,
} from "@/lib/mock-events-store";
import {
  getMockAboutContentStore,
  getMockHomeContentStore,
  getMockLegalContentStore,
  getMockTestimonialsArray,
  isMockAboutContentSeeded,
  isMockHomeContentSeeded,
  isMockLegalContentSeeded,
  isMockTestimonialsSeeded,
  resetMockAboutContentToSeed,
  resetMockTestimonialsToSeed,
  resetMockHomeContentToSeed,
  setMockAboutContentSeeded,
  setMockHomeContentSeeded,
  setMockLegalContentSeeded,
  setMockTestimonialsSeeded,
} from "@/lib/mock-site-content-store";
import {
  adminTestimonialToPublic,
  isTestimonialPublished,
  sortTestimonialsForDisplay,
  bumpTestimonialSortOrders,
} from "@/lib/testimonials-map";
import {
  DEFAULT_EVENTS_SECTION,
  DEFAULT_HOME_CONTENT,
  DEFAULT_HOME_HERO,
  DEFAULT_IMPACT_CONTENT,
  DEFAULT_PROGRAMS_CONTENT,
  DEFAULT_TESTIMONIALS_SECTION,
  mergeAboutContent,
  mergeHomeContent,
  mergeImpactContent,
  mergeProgramsContent,
  withEventsListFallback,
  withTestimonialFallbacks,
} from "@/lib/site-content-fallbacks";
import { normalizeAboutValueIcon } from "@/lib/about-value-icons";
import { normalizeFounderCardTemplate } from "@/lib/founder-card-templates";
import { mergeLegalContent } from "@/lib/legal-content";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const MOCK_DOWNLOAD_TOKEN = "mock_token_dev";
export const MOCK_SUCCESS_ACCESS_TOKEN = "mock_access_dev";
const MOCK_ADMIN_TOKEN = "mock_admin_token";

type MockRevisionStore = Record<string, CmsRevision[]>;
const mockRevisions: MockRevisionStore = {};
let mockProgramsContent: ProgramsContent = {
  programs: DEFAULT_PROGRAMS_CONTENT.programs.map((p) => ({ ...p })),
};
let mockImpactContent: ImpactContent = {
  hero: { ...DEFAULT_IMPACT_CONTENT.hero },
  categories: DEFAULT_IMPACT_CONTENT.categories.map((c) => ({
    ...c,
    programs: [...c.programs],
  })),
  gallery: {
    ...DEFAULT_IMPACT_CONTENT.gallery,
    images: DEFAULT_IMPACT_CONTENT.gallery.images.map((img) => ({ ...img })),
  },
};
let mockCmsMedia: CmsMediaItem[] = [];

function pushMockRevision(entityType: string, entityId: string): CmsRevision {
  const key = `${entityType}:${entityId}`;
  const revision: CmsRevision = {
    id: `rev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    entityType,
    entityId,
    createdByAdminId: mockCurrentAdminUser?.id ?? "admin_mock_1",
    createdAt: new Date().toISOString(),
  };
  const list = mockRevisions[key] ?? [];
  list.unshift(revision);
  mockRevisions[key] = list.slice(0, 50);
  return revision;
}

function listMockRevisions(entityType: string, entityId: string): CmsRevision[] {
  return [...(mockRevisions[`${entityType}:${entityId}`] ?? [])];
}

type MockDonationRecord = {
  amount: number;
  category: string;
  paidAt: string;
  createdAt: string;
  type: "one-time";
  donationSource: DonationSourceKey;
  donorFirstName?: string;
  donorLastName?: string;
  email?: string;
  phone?: string;
  pan?: string;
  address?: string;
  status: "pending" | "paid" | "failed" | "refunded";
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  receiptNumber?: string;
  emailSentAt?: string | null;
  receiptRegeneratedAt?: string | null;
  certificateRegeneratedAt?: string | null;
  reconciliationStatus?: "open" | "reviewed" | "dismissed" | null;
  adminNotes?: string | null;
  reviewedAt?: string | null;
};

const mockDonations = new Map<string, MockDonationRecord>();
const mockContactMessages: AdminContactMessage[] = [];
const mockNewsletterSubscribers: AdminNewsletterSubscriber[] = [];
let mockSettings: AdminSettings | null = null;
const mockAdminUsers: AdminUserListItem[] = [];
let contactSeeded = false;
let newsletterSeeded = false;
let adminUsersSeeded = false;
let mockCurrentAdminUser: AdminUser | null = null;
const mockAuditLog: AuditLogItem[] = [];
let auditSeeded = false;

function seedMockAuditLog() {
  if (auditSeeded) return;
  auditSeeded = true;
  const now = Date.now();
  const samples: Array<Omit<AuditLogItem, "id" | "createdAt">> = [
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "admin.login",
      targetType: "admin_user",
      targetId: "admin_1",
      detail: null,
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "donation.reconcile",
      targetType: "donation",
      targetId: "don_mock_1",
      detail: JSON.stringify({ reconciliationStatus: "reviewed" }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "donations.export",
      targetType: "donations",
      targetId: null,
      detail: JSON.stringify({ count: 12 }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "event.create",
      targetType: "event",
      targetId: "evt_1",
      detail: JSON.stringify({ title: "Community medical camp" }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "event.update",
      targetType: "event",
      targetId: "evt_1",
      detail: JSON.stringify({ published: true }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "testimonial.create",
      targetType: "testimonial",
      targetId: "tst_1",
      detail: null,
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "contact.update",
      targetType: "contact_message",
      targetId: "msg_1",
      detail: JSON.stringify({ status: "closed" }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "admin_user.create",
      targetType: "admin_user",
      targetId: "admin_2",
      detail: JSON.stringify({ role: "viewer" }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "settings.update",
      targetType: "settings",
      targetId: "app",
      detail: JSON.stringify({ receiptPrefix: "AJA" }),
    },
    {
      adminEmail: "admin@ajaah.org",
      adminId: "admin_1",
      action: "home_content.update",
      targetType: "home_content",
      targetId: "home",
      detail: JSON.stringify({ section: "hero" }),
    },
  ];
  for (let i = 0; i < 30; i++) {
    const base = samples[i % samples.length];
    mockAuditLog.push({
      ...base,
      id: `audit_seed_${i}`,
      createdAt: new Date(now - i * 3600_000).toISOString(),
    });
  }
}

function mockEvents() {
  return getMockEventsArray();
}

function mockDownloadUrls(donationId: string) {
  const token = MOCK_DOWNLOAD_TOKEN;
  return {
    downloadToken: token,
    successToken: MOCK_SUCCESS_ACCESS_TOKEN,
    receiptDownloadUrl: `/documents/receipt/${donationId}?token=${token}`,
    certificateDownloadUrl: `/documents/certificate/${donationId}?token=${token}`,
  };
}

function seedMockSettings(): AdminSettings {
  if (mockSettings) return mockSettings;
  mockSettings = {
    foundationName: siteDefaults.name,
    foundationLegalStatus: siteDefaults.legalStatus,
    foundationAddress: siteDefaults.address.full,
    foundationPan: "AACTA1234A",
    foundationEmail: siteDefaults.contact.email,
    foundationPhone: siteDefaults.contact.phone,
    registration80G: "AACTA1234A/80G/2024-25",
    registration80GValidFrom: "2020-04-01",
    registration80GValidTo: "2026-03-31",
    financialYear: "2025-26",
    lastReceiptNumber: 42,
    logoUrl: "",
    signatoryName: "Authorized Signatory",
    signatoryTitle: `Director, ${siteDefaults.name}`,
    receiptPrefix: "AH",
    yearsOfService: 4,
    projectsCompleted: 10,
    livesImpacted: 50000,
    directToProgramsPercent: 95,
  };
  return mockSettings;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}

function anonymizeDonorName(firstName?: string, lastName?: string): string {
  const first = firstName?.trim() ?? "";
  const last = lastName?.trim() ?? "";
  if (!first && !last) return "Anonymous";
  const lastInitial = last ? `${last[0].toUpperCase()}.` : "";
  return lastInitial ? `${first} ${lastInitial}` : first;
}

function mockDonationsForTrends(): Array<{
  amount: number;
  status: MockDonationRecord["status"];
  paidAt: string;
  createdAt: string;
}> {
  seedMockAdminDonations();
  return [...mockDonations.values()].map((record) => ({
    amount: record.amount,
    status: record.status,
    paidAt: record.paidAt,
    createdAt: record.createdAt,
  }));
}

function sumPaidSinceYearStart(): number {
  seedMockAdminDonations();
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  return [...mockDonations.values()]
    .filter(
      (record) =>
        record.status === "paid" && new Date(record.paidAt) >= startOfYear
    )
    .reduce((sum, record) => sum + record.amount, 0);
}

function seedMockAdminUsers() {
  if (adminUsersSeeded) return;
  adminUsersSeeded = true;
  const creds = getMockAdminCredentials();
  const now = new Date().toISOString();
  mockAdminUsers.push(
    {
      id: "admin_mock_1",
      email: creds.email,
      name: "Admin User",
      role: "super_admin",
      mfaEnabled: false,
      lastLoginAt: now,
      createdAt: new Date(Date.now() - 86400000 * 90).toISOString(),
    },
    {
      id: "admin_mock_2",
      email: "finance@ajaah.org",
      name: "Finance Manager",
      role: "finance",
      mfaEnabled: false,
      lastLoginAt: new Date(Date.now() - 86400000).toISOString(),
      createdAt: new Date(Date.now() - 86400000 * 60).toISOString(),
    },
    {
      id: "admin_mock_3",
      email: "viewer@ajaah.org",
      name: "Operations Viewer",
      role: "viewer",
      mfaEnabled: false,
      lastLoginAt: null,
      createdAt: new Date(Date.now() - 86400000 * 30).toISOString(),
    }
  );
}

function getMockAdminUser(email: string): AdminUser {
  seedMockAdminUsers();
  const found = mockAdminUsers.find((u) => u.email === email);
  if (found) {
    return {
      id: found.id,
      email: found.email,
      name: found.name,
      role: found.role,
      mfaEnabled: found.mfaEnabled ?? false,
    };
  }
  const creds = getMockAdminCredentials();
  return {
    id: "admin_mock_1",
    email: creds.email,
    name: "Admin User",
    role: "super_admin",
    mfaEnabled: false,
  };
}

function assertFinanceOrSuper(token: string) {
  assertAdminToken(token);
  const role = mockCurrentAdminUser?.role ?? "super_admin";
  if (role !== "finance" && role !== "super_admin" && role !== "editor") {
    throw new Error("Forbidden");
  }
}

function assertCmsWriter(token: string) {
  assertFinanceOrSuper(token);
}

function assertSuperAdminRole(token: string) {
  assertAdminToken(token);
  const role = mockCurrentAdminUser?.role ?? "super_admin";
  if (role !== "super_admin") {
    throw new Error("Forbidden");
  }
}

function assertSuperAdmin(token: string) {
  assertSuperAdminRole(token);
}

function getMockAdminCredentials() {
  return {
    email: process.env.NEXT_PUBLIC_ADMIN_MOCK_EMAIL ?? "admin@ajaah.org",
    password: process.env.NEXT_PUBLIC_ADMIN_MOCK_PASSWORD ?? "changeme",
  };
}

function assertAdminToken(token: string) {
  if (token !== MOCK_ADMIN_TOKEN) {
    throw new Error("Unauthorized");
  }
}

function toListItem(id: string, record: MockDonationRecord): AdminDonationListItem {
  const donorName =
    record.donorFirstName && record.donorLastName
      ? `${record.donorFirstName} ${record.donorLastName}`
      : "Anonymous Donor";
  const stripPii = mockCurrentAdminUser?.role === "viewer";
  return {
    id,
    donorName,
    email: stripPii ? "—" : (record.email ?? "—"),
    amount: record.amount,
    category: record.category,
    status: record.status,
    paidAt: record.status === "paid" ? record.paidAt : null,
    type: record.type,
    createdAt: record.createdAt,
    receiptNumber: record.receiptNumber ?? null,
    razorpayOrderId: stripPii ? null : (record.razorpayOrderId ?? null),
    razorpayPaymentId: stripPii ? null : (record.razorpayPaymentId ?? null),
    reconciliationStatus:
      record.reconciliationStatus ??
      (record.status === "pending" || record.status === "failed" ? "open" : null),
    adminNotes: stripPii ? null : (record.adminNotes ?? null),
  };
}

function seedMockContactMessages() {
  if (contactSeeded) return;
  contactSeeded = true;
  mockContactMessages.push(
    {
      id: "msg_seed_001",
      name: "Ravi Kumar",
      email: "ravi@example.com",
      phone: "9123456780",
      message: "I would like to volunteer for the upcoming health camp.",
      source: "volunteer",
      status: "new",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: "msg_seed_002",
      name: "Meera Nair",
      email: "meera@example.com",
      message: "Please share more details about corporate partnership opportunities.",
      source: "contact",
      status: "in_progress",
      adminNotes: "Sent partnership deck",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
    },
    {
      id: "msg_seed_003",
      name: "Arjun Desai",
      email: "arjun@example.com",
      message: "How can I sponsor a child's education through your foundation?",
      source: "home",
      status: "closed",
      handledAt: new Date(Date.now() - 86400000).toISOString(),
      createdAt: new Date(Date.now() - 259200000).toISOString(),
    }
  );
}

function seedMockNewsletterSubscribers() {
  if (newsletterSeeded) return;
  newsletterSeeded = true;
  mockNewsletterSubscribers.push(
    {
      id: "sub_seed_001",
      email: "subscriber1@example.com",
      subscribedAt: new Date(Date.now() - 604800000).toISOString(),
    },
    {
      id: "sub_seed_002",
      email: "subscriber2@example.com",
      subscribedAt: new Date(Date.now() - 1209600000).toISOString(),
    },
    {
      id: "sub_seed_003",
      email: "inactive@example.com",
      subscribedAt: new Date(Date.now() - 2592000000).toISOString(),
      unsubscribedAt: new Date(Date.now() - 864000000).toISOString(),
    }
  );
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? null : 0;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

function trendFrom(current: number, previous: number, label: string) {
  const value = pctChange(current, previous);
  if (value === null) return { value: null, label: "no prior period" };
  return { value, label };
}

function mockAdminStatsTrends(now: Date) {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfDay);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
  const sameDayLastYear = new Date(
    now.getFullYear() - 1,
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59
  );
  const weekAgo = new Date(now.getTime() - 7 * 86400000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 86400000);
  const dayAgo = new Date(now.getTime() - 86400000);
  const twoDaysAgo = new Date(now.getTime() - 2 * 86400000);

  const paid = [...mockDonations.values()].filter((r) => r.status === "paid");
  const sumPaidBetween = (since: Date, until?: Date) =>
    paid
      .filter((r) => {
        const paidAt = new Date(r.paidAt);
        return paidAt >= since && (until ? paidAt < until : true);
      })
      .reduce((sum, r) => sum + r.amount, 0);

  const countStatusBetween = (
    status: "pending" | "failed" | "paid",
    since: Date,
    until: Date,
    field: "createdAt" | "paidAt" = "createdAt"
  ) =>
    [...mockDonations.values()].filter((r) => {
      if (r.status !== status) return false;
      const at = new Date(field === "paidAt" ? r.paidAt : r.createdAt);
      return at >= since && at < until;
    }).length;

  const raisedToday = sumPaidBetween(startOfDay);
  const raisedYesterday = sumPaidBetween(startOfYesterday, startOfDay);
  const raisedMonth = sumPaidBetween(startOfMonth);
  const elapsedMs = now.getTime() - startOfMonth.getTime();
  const samePeriodLastMonthEnd = new Date(
    Math.min(startOfLastMonth.getTime() + elapsedMs, startOfMonth.getTime())
  );
  const raisedLastMonth = sumPaidBetween(
    startOfLastMonth,
    samePeriodLastMonthEnd
  );
  const raisedYear = sumPaidBetween(startOfYear);
  const raisedLastYearYtd = sumPaidBetween(startOfLastYear, sameDayLastYear);

  return {
    raisedToday: trendFrom(raisedToday, raisedYesterday, "vs yesterday"),
    raisedMonth: trendFrom(raisedMonth, raisedLastMonth, "vs last month"),
    raisedYear: trendFrom(raisedYear, raisedLastYearYtd, "vs last year"),
    donationCount: trendFrom(
      countStatusBetween("paid", weekAgo, now, "paidAt"),
      countStatusBetween("paid", twoWeeksAgo, weekAgo, "paidAt"),
      "vs last week"
    ),
    pendingCount: trendFrom(
      countStatusBetween("pending", dayAgo, now),
      countStatusBetween("pending", twoDaysAgo, dayAgo),
      "vs yesterday"
    ),
    failedCount: trendFrom(
      countStatusBetween("failed", weekAgo, now),
      countStatusBetween("failed", twoWeeksAgo, weekAgo),
      "vs last week"
    ),
  };
}

function aggregateMonthlyTrend(year: number): AdminMonthlyTrendResponse {
  seedMockAdminDonations();
  const amounts = new Array(12).fill(0) as number[];

  for (const record of mockDonations.values()) {
    if (record.status !== "paid") continue;
    const paid = new Date(record.paidAt);
    if (paid.getFullYear() !== year) continue;
    amounts[paid.getMonth()] += record.amount;
  }

  return {
    year,
    points: amounts.map((amount, index) => ({
      monthKey: formatMonthKey(year, index + 1),
      monthLabel: formatMonthLabel(index + 1),
      amount,
    })),
  };
}

function aggregateDonationSources(
  year: number,
  month: number | null
): AdminDonationSourcesResponse {
  seedMockAdminDonations();
  const now = new Date();
  const sums = new Map<DonationSourceKey, number>();
  for (const key of DONATION_SOURCE_KEYS) {
    sums.set(key, 0);
  }

  for (const record of mockDonations.values()) {
    if (record.status !== "paid") continue;
    const paid = new Date(record.paidAt);
    if (paid.getFullYear() !== year) continue;

    if (month !== null) {
      if (paid.getMonth() + 1 !== month) continue;
    } else {
      const ytdStart = new Date(year, 0, 1);
      const ytdEnd =
        year === now.getFullYear()
          ? now
          : new Date(year, 11, 31, 23, 59, 59, 999);
      if (paid < ytdStart || paid > ytdEnd) continue;
    }

    const key = record.donationSource;
    sums.set(key, (sums.get(key) ?? 0) + record.amount);
  }

  const totalAmount = [...sums.values()].reduce((sum, n) => sum + n, 0);

  const sources = DONATION_SOURCE_KEYS.flatMap((key) => {
    const amount = sums.get(key) ?? 0;
    if (amount <= 0) return [];
    const value =
      totalAmount > 0
        ? Math.round((amount / totalAmount) * 1000) / 10
        : 0;
    return [
      {
        key,
        name: DONATION_SOURCE_LABELS[key],
        amount,
        value,
      },
    ];
  });

  return {
    year,
    month,
    periodLabel: formatPeriodLabel(year, month),
    totalAmount,
    sources,
  };
}

function aggregateDonationCategories(
  year: number,
  month: number | null
): AdminDonationCategoriesResponse {
  seedMockAdminDonations();
  const now = new Date();
  const sums = new Map<string, number>();
  for (const cat of DONATION_CATEGORIES) {
    sums.set(cat.value, 0);
  }

  for (const record of mockDonations.values()) {
    if (record.status !== "paid") continue;
    const paid = new Date(record.paidAt);
    if (paid.getFullYear() !== year) continue;

    if (month !== null) {
      if (paid.getMonth() + 1 !== month) continue;
    } else {
      const ytdStart = new Date(year, 0, 1);
      const ytdEnd =
        year === now.getFullYear()
          ? now
          : new Date(year, 11, 31, 23, 59, 59, 999);
      if (paid < ytdStart || paid > ytdEnd) continue;
    }

    if (!sums.has(record.category)) continue;
    sums.set(record.category, (sums.get(record.category) ?? 0) + record.amount);
  }

  const totalAmount = [...sums.values()].reduce((sum, n) => sum + n, 0);

  const categories = DONATION_CATEGORIES.flatMap((cat) => {
    const amount = sums.get(cat.value) ?? 0;
    if (amount <= 0) return [];
    const value =
      totalAmount > 0
        ? Math.round((amount / totalAmount) * 1000) / 10
        : 0;
    return [
      {
        key: cat.value,
        name: cat.label,
        amount,
        value,
      },
    ];
  });

  return {
    year,
    month,
    periodLabel: formatPeriodLabel(year, month),
    totalAmount,
    categories,
  };
}

function seedMockAdminDonations() {
  if (mockDonations.size > 0) return;

  const seeds: Array<[string, MockDonationRecord]> = [
    [
      "don_seed_001",
      {
        amount: 5000,
        category: "healthcare",
        createdAt: new Date(2026, 6, 15, 8, 30).toISOString(),
        paidAt: new Date(2026, 6, 15, 10, 30).toISOString(),
        type: "one-time",
        donationSource: "individual",
        donorFirstName: "Amit",
        donorLastName: "Sharma",
        email: "amit@example.com",
        phone: "9876543210",
        pan: "ABCDE1234F",
        status: "paid",
        razorpayOrderId: "order_seed_001",
        razorpayPaymentId: "pay_seed_001",
        receiptNumber: "AH/2025-26/0001",
        emailSentAt: new Date(Date.now() - 2 * 3600000).toISOString(),
      },
    ],
    [
      "don_seed_002",
      {
        amount: 2500,
        category: "education",
        createdAt: new Date(2026, 2, 10, 9, 0).toISOString(),
        paidAt: new Date(2026, 2, 12, 11, 0).toISOString(),
        type: "one-time",
        donationSource: "corporate",
        donorFirstName: "Priya",
        donorLastName: "Menon",
        email: "priya@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_002",
        razorpayPaymentId: "pay_seed_002",
        receiptNumber: "AH/2025-26/0002",
        emailSentAt: new Date(Date.now() - 86400000).toISOString(),
      },
    ],
    [
      "don_seed_003",
      {
        amount: 1000,
        category: "general",
        createdAt: new Date(2026, 4, 5, 14, 0).toISOString(),
        paidAt: new Date(2026, 4, 8, 16, 0).toISOString(),
        type: "one-time",
        donationSource: "online",
        donorFirstName: "Rajesh",
        donorLastName: "Kumar",
        email: "rajesh@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_003",
        razorpayPaymentId: "pay_seed_003",
        receiptNumber: "AH/2025-26/0003",
        emailSentAt: new Date(Date.now() - 172800000).toISOString(),
      },
    ],
    [
      "don_seed_004",
      {
        amount: 10000,
        category: "nutrition",
        createdAt: new Date(2026, 2, 20, 10, 0).toISOString(),
        paidAt: new Date(2026, 2, 22, 12, 0).toISOString(),
        type: "one-time",
        donationSource: "csr",
        donorFirstName: "Sunita",
        donorLastName: "Patel",
        email: "sunita@example.com",
        pan: "FGHIJ5678K",
        status: "paid",
        razorpayOrderId: "order_seed_004",
        razorpayPaymentId: "pay_seed_004",
        receiptNumber: "AH/2025-26/0004",
        emailSentAt: new Date(Date.now() - 259200000).toISOString(),
      },
    ],
    [
      "don_seed_005",
      {
        amount: 500,
        category: "environment",
        createdAt: new Date(Date.now() - 3600000).toISOString(),
        paidAt: new Date().toISOString(),
        type: "one-time",
        donationSource: "online",
        donorFirstName: "Vikram",
        donorLastName: "Reddy",
        email: "vikram@example.com",
        status: "pending",
        razorpayOrderId: "order_seed_005",
        emailSentAt: null,
      },
    ],
    [
      "don_seed_006",
      {
        amount: 2000,
        category: "livelihood",
        createdAt: new Date(2026, 1, 1, 9, 0).toISOString(),
        paidAt: new Date(2026, 1, 2, 10, 0).toISOString(),
        type: "one-time",
        donationSource: "events",
        donorFirstName: "Ananya",
        donorLastName: "Iyer",
        email: "ananya@example.com",
        pan: "KLMNO9012P",
        status: "failed",
        razorpayOrderId: "order_seed_006",
        emailSentAt: null,
      },
    ],
    [
      "don_seed_007",
      {
        amount: 3000,
        category: "healthcare",
        createdAt: new Date(2026, 5, 1, 11, 0).toISOString(),
        paidAt: new Date(2026, 5, 2, 12, 0).toISOString(),
        type: "one-time",
        donationSource: "individual",
        donorFirstName: "Karan",
        donorLastName: "Mehta",
        email: "karan@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_007",
        razorpayPaymentId: "pay_seed_007",
        receiptNumber: "AH/2025-26/0005",
        emailSentAt: new Date(Date.now() - 432000000).toISOString(),
      },
    ],
    [
      "don_seed_008",
      {
        amount: 1500,
        category: "education",
        createdAt: new Date(2026, 4, 20, 8, 0).toISOString(),
        paidAt: new Date(2026, 4, 21, 9, 0).toISOString(),
        type: "one-time",
        donationSource: "online",
        donorFirstName: "Deepa",
        donorLastName: "Singh",
        email: "deepa@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_008",
        razorpayPaymentId: "pay_seed_008",
        receiptNumber: "AH/2025-26/0006",
        emailSentAt: new Date(Date.now() - 518400000).toISOString(),
      },
    ],
    [
      "don_seed_009",
      {
        amount: 750,
        category: "general",
        createdAt: new Date(2026, 3, 15, 14, 0).toISOString(),
        paidAt: new Date(2026, 3, 16, 10, 0).toISOString(),
        type: "one-time",
        donationSource: "online",
        donorFirstName: "Rohit",
        donorLastName: "Gupta",
        email: "rohit@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_009",
        razorpayPaymentId: "pay_seed_009",
        receiptNumber: "AH/2025-26/0007",
        emailSentAt: new Date(Date.now() - 604800000).toISOString(),
      },
    ],
    [
      "don_seed_010",
      {
        amount: 1200,
        category: "nutrition",
        createdAt: new Date(2026, 2, 5, 10, 0).toISOString(),
        paidAt: new Date(2026, 2, 6, 11, 0).toISOString(),
        type: "one-time",
        donationSource: "csr",
        donorFirstName: "Neha",
        donorLastName: "Kapoor",
        email: "neha@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_010",
        razorpayPaymentId: "pay_seed_010",
        receiptNumber: "AH/2025-26/0008",
        emailSentAt: new Date(Date.now() - 691200000).toISOString(),
      },
    ],
    [
      "don_seed_011",
      {
        amount: 800,
        category: "environment",
        createdAt: new Date(Date.now() - 7200000).toISOString(),
        paidAt: new Date(Date.now() - 7200000).toISOString(),
        type: "one-time",
        donationSource: "online",
        donorFirstName: "Suresh",
        donorLastName: "Naidu",
        email: "suresh@example.com",
        status: "pending",
        razorpayOrderId: "order_seed_011",
        emailSentAt: null,
      },
    ],
    [
      "don_seed_012",
      {
        amount: 4500,
        category: "healthcare",
        createdAt: new Date(2026, 0, 10, 9, 0).toISOString(),
        paidAt: new Date(2026, 0, 12, 10, 0).toISOString(),
        type: "one-time",
        donationSource: "corporate",
        donorFirstName: "Lakshmi",
        donorLastName: "Venkatesh",
        email: "lakshmi@example.com",
        pan: "PQRST3456U",
        status: "paid",
        razorpayOrderId: "order_seed_012",
        razorpayPaymentId: "pay_seed_012",
        receiptNumber: "AH/2025-26/0009",
        emailSentAt: new Date(Date.now() - 777600000).toISOString(),
      },
    ],
    [
      "don_seed_013",
      {
        amount: 600,
        category: "livelihood",
        createdAt: new Date(2025, 11, 20, 15, 0).toISOString(),
        paidAt: new Date(2025, 11, 22, 10, 0).toISOString(),
        type: "one-time",
        donationSource: "events",
        donorFirstName: "Manish",
        donorLastName: "Joshi",
        email: "manish@example.com",
        status: "refunded",
        razorpayOrderId: "order_seed_013",
        razorpayPaymentId: "pay_seed_013",
        receiptNumber: "AH/2025-26/0010",
        emailSentAt: new Date(Date.now() - 864000000).toISOString(),
      },
    ],
    [
      "don_seed_014",
      {
        amount: 3500,
        category: "education",
        createdAt: new Date(2026, 5, 20, 8, 0).toISOString(),
        paidAt: new Date(2026, 5, 21, 9, 0).toISOString(),
        type: "one-time",
        donationSource: "individual",
        donorFirstName: "Pooja",
        donorLastName: "Rao",
        email: "pooja@example.com",
        status: "paid",
        razorpayOrderId: "order_seed_014",
        razorpayPaymentId: "pay_seed_014",
        receiptNumber: "AH/2025-26/0011",
        emailSentAt: new Date(Date.now() - 950400000).toISOString(),
      },
    ],
    [
      "don_seed_015",
      {
        amount: 900,
        category: "general",
        createdAt: new Date(Date.now() - 1800000).toISOString(),
        paidAt: new Date(Date.now() - 1800000).toISOString(),
        type: "one-time",
        donationSource: "online",
        donorFirstName: "Arun",
        donorLastName: "Pillai",
        email: "arun@example.com",
        status: "failed",
        razorpayOrderId: "order_seed_015",
        emailSentAt: null,
        reconciliationStatus: "reviewed",
        reviewedAt: new Date(Date.now() - 86400000).toISOString(),
      },
    ],
  ];

  for (const [id, record] of seeds) {
    mockDonations.set(id, record);
  }
}

function applyDonationFilters(
  items: AdminDonationListItem[],
  records: Map<string, MockDonationRecord>,
  filters?: AdminDonationFilters | AdminDonationListParams
) {
  let result = items;

  if (filters && "statuses" in filters && filters.statuses?.length) {
    const allowed = new Set(filters.statuses);
    result = result.filter((item) => allowed.has(item.status));
  } else if (filters?.status) {
    result = result.filter((item) => item.status === filters.status);
  }

  if (
    filters &&
    "reconciliationStatus" in filters &&
    filters.reconciliationStatus
  ) {
    result = result.filter(
      (item) => (item.reconciliationStatus ?? "open") === filters.reconciliationStatus
    );
  }

  if (filters?.category) {
    result = result.filter((item) => item.category === filters.category);
  }

  if (filters?.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    result = result.filter((item) => {
      const record = records.get(item.id);
      return (
        item.donorName.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q) ||
        item.id.toLowerCase().includes(q) ||
        record?.receiptNumber?.toLowerCase().includes(q) ||
        record?.razorpayPaymentId?.toLowerCase().includes(q)
      );
    });
  }

  if (filters?.dateFrom || filters?.dateTo) {
    result = result.filter((item) => {
      const dateStr = item.paidAt ?? item.createdAt;
      if (!dateStr) return false;
      const date = new Date(dateStr);
      if (filters.dateFrom && date < new Date(`${filters.dateFrom}T00:00:00`)) {
        return false;
      }
      if (filters.dateTo && date > new Date(`${filters.dateTo}T23:59:59`)) {
        return false;
      }
      return true;
    });
  }

  return result;
}

function sortDonationListItems(
  items: AdminDonationListItem[],
  sortBy: "createdAt" | "amount" = "createdAt",
  sortOrder: "asc" | "desc" = "desc"
) {
  return [...items].sort((a, b) => {
    if (sortBy === "amount") {
      return sortOrder === "asc" ? a.amount - b.amount : b.amount - a.amount;
    }
    const aTime = new Date(a.createdAt ?? a.paidAt ?? 0).getTime();
    const bTime = new Date(b.createdAt ?? b.paidAt ?? 0).getTime();
    return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
  });
}

function getFilteredSortedDonations(
  filters?: AdminDonationFilters | AdminDonationListParams
) {
  seedMockAdminDonations();
  let items = [...mockDonations.entries()].map(([id, record]) =>
    toListItem(id, record)
  );
  items = applyDonationFilters(items, mockDonations, filters);
  const sortBy =
    filters && "sortBy" in filters && filters.sortBy ? filters.sortBy : "createdAt";
  const sortOrder =
    filters && "sortOrder" in filters && filters.sortOrder
      ? filters.sortOrder
      : "desc";
  return sortDonationListItems(items, sortBy, sortOrder);
}

function paginateDonations(
  items: AdminDonationListItem[],
  page = 1,
  pageSize = 10
): AdminPaginatedDonationsResponse {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total,
    page: safePage,
    pageSize,
    totalPages,
  };
}

function seedMockEvents() {
  if (isMockEventsSeeded()) return;
  setMockEventsSeeded(true);
  const events = mockEvents();
  events.push(
    {
      id: "evt_seed_1",
      title: "Community Food Drive",
      eventDate: "2025-12-15",
      date: formatEventDateFromIso("2025-12-15"),
      description:
        "Distributed essential food supplies to over 200 families in need across the community.",
      status: "completed",
      items: "500+ Food Packets",
      images: [assets.contact[0]],
      published: true,
      featured: false,
      sortOrder: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: "evt_seed_2",
      title: "Educational Kit Distribution",
      eventDate: "2025-11-20",
      date: formatEventDateFromIso("2025-11-20"),
      description:
        "Provided school supplies and educational materials to underprivileged children.",
      status: "completed",
      items: "300+ Kits",
      images: [assets.programs.education],
      published: true,
      featured: false,
      sortOrder: 2,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: "evt_seed_3",
      title: "Health Camp 2026",
      eventDate: "2026-03-10",
      date: formatEventDateFromIso("2026-03-10"),
      description:
        "Free health checkups and medical consultations for rural communities.",
      status: "upcoming",
      items: "Free Medical Camp",
      images: [assets.programs.healthcare],
      heroImage: assets.eventsHero,
      published: true,
      featured: true,
      sortOrder: 3,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: "evt_seed_4",
      title: "Community Tree Plantation Drive",
      eventDate: "2026-04-22",
      date: formatEventDateFromIso("2026-04-22"),
      description:
        "Join volunteers to plant native trees and restore green cover in local neighborhoods.",
      status: "upcoming",
      items: "1,000+ saplings",
      images: [assets.programs.goshala],
      published: true,
      featured: false,
      sortOrder: 4,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  );
}

function seedMockHomeContent() {
  if (isMockHomeContentSeeded()) return;
  setMockHomeContentSeeded(true);
  const store = getMockHomeContentStore();
  store.hero = {
    ...DEFAULT_HOME_HERO,
    headingLines: [...DEFAULT_HOME_HERO.headingLines],
    heroImage: "",
  };
  store.eventsSection = { ...DEFAULT_EVENTS_SECTION };
  store.testimonialsSection = { ...DEFAULT_TESTIMONIALS_SECTION };
  store.impact = {
    ...DEFAULT_HOME_CONTENT.impact,
    metrics: DEFAULT_HOME_CONTENT.impact.metrics.map((m) => ({ ...m })),
  };
  store.aboutStory = {
    ...DEFAULT_HOME_CONTENT.aboutStory,
    highlightKeywords: [...DEFAULT_HOME_CONTENT.aboutStory.highlightKeywords],
    timeline: DEFAULT_HOME_CONTENT.aboutStory.timeline.map((t) => ({ ...t })),
  };
  store.donateSection = { ...DEFAULT_HOME_CONTENT.donateSection };
  store.featuredPrograms = {
    ...DEFAULT_HOME_CONTENT.featuredPrograms,
    slugs: [...DEFAULT_HOME_CONTENT.featuredPrograms.slugs],
  };
  store.miniGallery = {
    ...DEFAULT_HOME_CONTENT.miniGallery,
    images: DEFAULT_HOME_CONTENT.miniGallery.images.map((img) => ({ ...img })),
  };
  store.updatedAt = new Date().toISOString();
}

function seedMockAboutContent() {
  if (isMockAboutContentSeeded()) return;
  setMockAboutContentSeeded(true);
  const store = getMockAboutContentStore();
  store.updatedAt = new Date().toISOString();
}

function seedMockLegalContent() {
  if (isMockLegalContentSeeded()) return;
  setMockLegalContentSeeded(true);
  const store = getMockLegalContentStore();
  store.updatedAt = new Date().toISOString();
}

function seedMockTestimonials() {
  if (isMockTestimonialsSeeded()) return;
  setMockTestimonialsSeeded(true);
  const items = getMockTestimonialsArray();
  const now = new Date().toISOString();
  homeTestimonials.forEach((t, i) => {
    items.push({
      id: `tst_seed_${i + 1}`,
      quote: t.quote,
      name: t.name,
      role: t.role,
      image: t.image,
      published: true,
      sortOrder: i + 1,
      createdAt: now,
      updatedAt: now,
    });
  });
}

const MAX_EVENT_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_EVENT_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

async function readFileAsDataUrl(file: File): Promise<string> {
  if (typeof FileReader !== "undefined") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }
  const bytes = await file.arrayBuffer();
  const base64 = Buffer.from(bytes).toString("base64");
  const mime = file.type || "application/octet-stream";
  return `data:${mime};base64,${base64}`;
}

function collectEventNotifyAudience() {
  seedMockAdminDonations();
  seedMockNewsletterSubscribers();
  const donorEmails = new Set<string>();
  for (const record of mockDonations.values()) {
    if (record.status === "paid" && record.email) {
      donorEmails.add(record.email.toLowerCase());
    }
  }
  const newsletterEmails = new Set<string>();
  for (const sub of mockNewsletterSubscribers) {
    if (!sub.unsubscribedAt) {
      newsletterEmails.add(sub.email.toLowerCase());
    }
  }
  const deduped = new Set<string>([...donorEmails, ...newsletterEmails]);
  return {
    donorCount: donorEmails.size,
    newsletterCount: newsletterEmails.size,
    totalDeduped: deduped.size,
  };
}

export const mockApiImpl = {
  async submitContact(payload: ContactPayload) {
    await delay(800);
    seedMockContactMessages();
    const id = `msg_${Date.now()}`;
    mockContactMessages.unshift({
      id,
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      message: payload.message,
      source: payload.source,
      createdAt: new Date().toISOString(),
    });
    return { success: true, id };
  },

  async sendOtp(payload: SendOtpPayload) {
    await delay(600);
    return { success: true, message: `OTP sent to ${payload.channel} successfully` };
  },

  async verifyOtp(payload: VerifyOtpPayload): Promise<VerifyOtpResponse> {
    await delay(600);
    if (payload.otp === "123456" || payload.otp === "888888" || payload.otp.length === 6) {
      return { success: true, verificationToken: `mock_otp_verified_${payload.channel}_token` };
    }
    throw new Error(`Invalid ${payload.channel} OTP. Try entering 123456.`);
  },

  async sendDonationEmailCustom(donationId: string, payload: SendEmailCustomPayload) {
    await delay(500);
    seedMockAdminDonations();
    const record = mockDonations.get(donationId);
    if (record) {
      record.emailSentAt = new Date().toISOString();
      record.email = payload.email;
    }
    return { success: true, message: `Email sent successfully to ${payload.email}` };
  },

  async subscribeNewsletter(payload: NewsletterPayload) {
    await delay(600);
    seedMockNewsletterSubscribers();
    const existing = mockNewsletterSubscribers.find(
      (s) => s.email === payload.email && !s.unsubscribedAt
    );
    if (!existing) {
      mockNewsletterSubscribers.unshift({
        id: `sub_${Date.now()}`,
        email: payload.email,
        subscribedAt: new Date().toISOString(),
      });
    }
    return { success: true };
  },

  async unsubscribeNewsletter(payload: NewsletterUnsubscribePayload) {
    await delay(600);
    seedMockNewsletterSubscribers();
    const existing = mockNewsletterSubscribers.find(
      (s) => s.email.toLowerCase() === payload.email.toLowerCase()
    );
    if (existing && !existing.unsubscribedAt) {
      existing.unsubscribedAt = new Date().toISOString();
    }
    return { success: true };
  },

  async getAdminLoginContent(): Promise<AdminLoginContent> {
    await delay(300);
    const settings = seedMockSettings();
    return {
      foundationName: settings.foundationName,
      appVersion: "mock",
      tagline: adminLoginContent.tagline,
      headline: adminLoginContent.headline,
      subtext: adminLoginContent.subtext,
      image: adminLoginContent.image,
      imageAlt: adminLoginContent.imageAlt,
      formTitle: adminLoginContent.formTitle,
      formSubtitle: adminLoginContent.formSubtitle,
      trustHeading: adminLoginContent.trustHeading,
      trustBody: adminLoginContent.trustBody,
      hopeMessage: adminLoginContent.hopeMessage,
      pillars: adminLoginContent.pillars.map((p) => p.label),
      stats: [
        {
          id: "lives",
          label: "Lives Impacted",
          value: settings.livesImpacted,
        },
        {
          id: "volunteers",
          label: "Volunteers",
          value: 500,
        },
        {
          id: "communities",
          label: "Communities",
          value: 50,
        },
      ],
    };
  },

  async getStats(): Promise<Stats> {
    await delay(300);
    const settings = seedMockSettings();
    return {
      yearsOfService: settings.yearsOfService,
      projectsCompleted: settings.projectsCompleted,
      livesImpacted: settings.livesImpacted,
      volunteers: 500,
      communities: 50,
      raisedThisYear: sumPaidSinceYearStart(),
      directToProgramsPercent: settings.directToProgramsPercent,
    };
  },

  async getEvents(): Promise<Event[]> {
    await delay(400);
    seedMockEvents();
    const events = mockEvents();
    return withEventsListFallback(
      events
        .filter((event) => isEventPublished(event))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map(adminEventToPublicEvent)
    );
  },

  async getRecentDonors(): Promise<RecentDonor[]> {
    await delay(300);
    seedMockAdminDonations();
    return [...mockDonations.entries()]
      .filter(([, record]) => record.status === "paid")
      .sort(
        (a, b) =>
          new Date(b[1].paidAt).getTime() - new Date(a[1].paidAt).getTime()
      )
      .slice(0, 10)
      .map(([, record]) => ({
        name: anonymizeDonorName(record.donorFirstName, record.donorLastName),
        amount: record.amount,
        createdAt: formatRelativeTime(record.paidAt ?? record.createdAt),
      }));
  },

  async createDonationOrder(
    payload: CreateOrderPayload
  ): Promise<CreateOrderResponse> {
    await delay(1000);
    const orderId = `order_mock_${Date.now()}`;
    mockDonations.set(orderId, {
      amount: payload.amount,
      category: payload.category,
      paidAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      type: payload.type,
      donationSource: "online",
      donorFirstName: payload.donor.firstName,
      donorLastName: payload.donor.lastName,
      email: payload.donor.email,
      phone: payload.donor.phone,
      pan: payload.donor.pan,
      status: "pending",
      razorpayOrderId: orderId,
      emailSentAt: null,
    });
    return {
      orderId,
      amount: payload.amount * 100,
      currency: "INR",
      razorpayKeyId: "rzp_test_mock_key",
    };
  },

  async verifyDonation(
    payload: VerifyPaymentPayload
  ): Promise<VerifyPaymentResponse> {
    await delay(800);
    const donationId = `don_${Date.now()}`;
    const pending = mockDonations.get(payload.razorpay_order_id);
    const now = new Date().toISOString();
    if (pending) {
      mockDonations.set(donationId, {
        ...pending,
        status: "paid",
        paidAt: now,
        razorpayPaymentId: payload.razorpay_payment_id,
        receiptNumber: `AH/2025-26/${String(mockDonations.size).padStart(4, "0")}`,
        emailSentAt: now,
      });
      mockDonations.delete(payload.razorpay_order_id);
    } else {
      mockDonations.set(donationId, {
        amount: 1000,
        category: "general",
        paidAt: now,
        createdAt: now,
        type: "one-time",
        donationSource: "online",
        status: "paid",
        razorpayPaymentId: payload.razorpay_payment_id,
        emailSentAt: now,
      });
    }
    return {
      success: true,
      donationId,
      ...mockDownloadUrls(donationId),
    };
  },

  async getDonationStatus(
    donationId: string,
    options: { token?: string; access?: string }
  ): Promise<DonationStatus> {
    await delay(400);
    const token = options.token;
    const access = options.access;
    const validToken = token === MOCK_DOWNLOAD_TOKEN;
    const validAccess = access === MOCK_SUCCESS_ACCESS_TOKEN;
    if (!validToken && !validAccess) {
      throw new Error("Invalid or expired download link.");
    }
    const record = mockDonations.get(donationId);
    const urls = mockDownloadUrls(donationId);
    return {
      donationId,
      amount: record?.amount ?? 1000,
      currency: "INR",
      category: record?.category ?? "general",
      paidAt: record?.paidAt ?? new Date().toISOString(),
      receiptDownloadUrl: urls.receiptDownloadUrl,
      certificateDownloadUrl: urls.certificateDownloadUrl,
      emailSentAt: record?.emailSentAt ?? null,
    };
  },

  async adminLogin(payload: AdminLoginPayload): Promise<AdminSession> {
    await delay(500);
    const creds = getMockAdminCredentials();
    seedMockAdminUsers();
    const known = mockAdminUsers.find(
      (u) => u.email === payload.email && payload.password === creds.password
    );
    if (
      !known &&
      (payload.email !== creds.email || payload.password !== creds.password)
    ) {
      throw new Error("Invalid email or password");
    }
    const user = getMockAdminUser(payload.email);
    mockCurrentAdminUser = user;
    mockAuditLog.unshift({
      id: `audit_${Date.now()}`,
      adminId: user.id,
      adminEmail: user.email,
      action: "admin.login",
      targetType: "admin_user",
      targetId: user.id,
      createdAt: new Date().toISOString(),
    });
    return { token: MOCK_ADMIN_TOKEN, user, mfaRequired: false };
  },

  async adminGetMe(token: string): Promise<AdminUser> {
    await delay(200);
    assertAdminToken(token);
    if (mockCurrentAdminUser) return mockCurrentAdminUser;
    if (typeof window !== "undefined") {
      const session = getAdminSession();
      if (session?.user) {
        mockCurrentAdminUser = session.user;
        return session.user;
      }
    }
    const creds = getMockAdminCredentials();
    return getMockAdminUser(creds.email);
  },

  async adminLogout(token: string) {
    await delay(100);
    assertAdminToken(token);
    mockCurrentAdminUser = null;
    return { success: true };
  },

  async adminGetStats(token: string): Promise<AdminStats> {
    await delay(300);
    assertAdminToken(token);
    seedMockAdminDonations();
    const paid = [...mockDonations.entries()].filter(
      ([, r]) => r.status === "paid"
    );
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    const sumSince = (since: Date) =>
      paid
        .filter(([, r]) => new Date(r.paidAt) >= since)
        .reduce((sum, [, r]) => sum + r.amount, 0);

    const byCategory: Record<string, number> = {};
    for (const cat of DONATION_CATEGORIES) {
      byCategory[cat.value] = 0;
    }
    for (const [, r] of paid) {
      byCategory[r.category] = (byCategory[r.category] ?? 0) + r.amount;
    }

    const windowCounts = computeAdminStatsWindowCounts(mockDonationsForTrends());

    return {
      raisedToday: sumSince(startOfDay),
      raisedMonth: sumSince(startOfMonth),
      raisedYear: sumSince(startOfYear),
      donationCount: windowCounts.donationCount,
      pendingCount: windowCounts.pendingCount,
      failedCount: windowCounts.failedCount,
      byCategory,
      trends: computeAdminStatsTrends(mockDonationsForTrends()),
    };
  },

  async adminGetDonationsSummary(
    token: string,
    filters?: AdminDonationFilters
  ): Promise<AdminDonationsSummary> {
    await delay(300);
    assertAdminToken(token);
    const items = getFilteredSortedDonations(filters);
    const filtered = Boolean(
      filters?.status ||
        filters?.search?.trim() ||
        filters?.category ||
        filters?.dateFrom ||
        filters?.dateTo
    );

    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfDay);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth =
      startOfMonth.getMonth() === 0
        ? new Date(startOfMonth.getFullYear() - 1, 11, 1)
        : new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() - 1, 1);

    const sumPaidBetween = (since: Date, until?: Date) =>
      items
        .filter((d) => {
          if (d.status !== "paid" || !d.paidAt) return false;
          const paidAt = new Date(d.paidAt);
          if (paidAt < since) return false;
          if (until && paidAt >= until) return false;
          return true;
        })
        .reduce((sum, d) => sum + d.amount, 0);

    const raisedToday = sumPaidBetween(startOfDay);
    const raisedMonth = sumPaidBetween(startOfMonth);
    const totalAmount = items.reduce((sum, d) => sum + d.amount, 0);
    const paidAmount = items
      .filter((d) => d.status === "paid")
      .reduce((sum, d) => sum + d.amount, 0);

    return {
      raisedToday,
      raisedMonth,
      matchCount: items.length,
      totalAmount,
      paidAmount,
      filtered,
      trends: filtered
        ? null
        : {
            raisedToday: (() => {
              const prev = sumPaidBetween(startOfYesterday, startOfDay);
              return trendFrom(raisedToday, prev, "vs yesterday");
            })(),
            raisedMonth: (() => {
              const elapsedMs = Date.now() - startOfMonth.getTime();
              const samePeriodLastMonthEnd = new Date(
                Math.min(
                  startOfLastMonth.getTime() + elapsedMs,
                  startOfMonth.getTime()
                )
              );
              const prev = sumPaidBetween(
                startOfLastMonth,
                samePeriodLastMonthEnd
              );
              return trendFrom(raisedMonth, prev, "vs last month");
            })(),
          },
    };
  },

  async adminGetMonthlyTrend(
    token: string,
    year?: number
  ): Promise<AdminMonthlyTrendResponse> {
    await delay(300);
    assertAdminToken(token);
    const targetYear = year ?? new Date().getFullYear();
    return aggregateMonthlyTrend(targetYear);
  },

  async adminGetDonationSources(
    token: string,
    params?: { year?: number; month?: number | null }
  ): Promise<AdminDonationSourcesResponse> {
    await delay(300);
    assertAdminToken(token);
    const targetYear = params?.year ?? new Date().getFullYear();
    const month = params?.month ?? null;
    return aggregateDonationSources(targetYear, month);
  },

  async adminGetDonationCategories(
    token: string,
    params?: { year?: number; month?: number | null }
  ): Promise<AdminDonationCategoriesResponse> {
    await delay(300);
    assertAdminToken(token);
    const targetYear = params?.year ?? new Date().getFullYear();
    const month = params?.month ?? null;
    return aggregateDonationCategories(targetYear, month);
  },

  async adminListDonations(
    token: string,
    filters?: AdminDonationFilters | AdminDonationListParams
  ): Promise<AdminDonationListItem[]> {
    await delay(400);
    assertAdminToken(token);
    return getFilteredSortedDonations(filters);
  },

  async adminExportDonationsCsv(
    token: string,
    filters?: AdminDonationFilters
  ): Promise<string> {
    await delay(400);
    assertFinanceOrSuper(token);
    const donations = getFilteredSortedDonations(filters);
    return donationsToCsv(donations);
  },

  async adminListDonationsPaginated(
    token: string,
    params?: AdminDonationListParams
  ): Promise<AdminPaginatedDonationsResponse> {
    await delay(400);
    assertAdminToken(token);
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const sorted = getFilteredSortedDonations(params);
    return paginateDonations(sorted, page, pageSize);
  },

  async adminGetDonation(
    token: string,
    id: string
  ): Promise<AdminDonationDetail> {
    await delay(300);
    assertAdminToken(token);
    seedMockAdminDonations();
    const record = mockDonations.get(id);
    if (!record) {
      throw new Error("Donation not found");
    }
    const base = toListItem(id, record);
    const urls =
      record.status === "paid" ? mockDownloadUrls(id) : null;
    const stripPii = mockCurrentAdminUser?.role === "viewer";
    return {
      ...base,
      type: record.type,
      createdAt: record.createdAt,
      phone: stripPii ? null : (record.phone ?? null),
      pan: stripPii ? null : (record.pan ?? null),
      razorpayOrderId: stripPii ? null : (record.razorpayOrderId ?? null),
      razorpayPaymentId: stripPii ? null : (record.razorpayPaymentId ?? null),
      receiptNumber: record.receiptNumber ?? null,
      emailSentAt: record.emailSentAt ?? null,
      receiptRegeneratedAt: record.receiptRegeneratedAt ?? null,
      certificateRegeneratedAt: record.certificateRegeneratedAt ?? null,
      receiptDownloadUrl: urls?.receiptDownloadUrl ?? null,
      certificateDownloadUrl: urls?.certificateDownloadUrl ?? null,
      adminNotes: stripPii ? null : (record.adminNotes ?? null),
      reviewedAt: record.reviewedAt ?? null,
      reconciliationStatus:
        record.reconciliationStatus ??
        (record.status === "pending" || record.status === "failed" ? "open" : null),
    };
  },

  async adminListContact(token: string): Promise<AdminContactMessage[]> {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockContactMessages();
    return [...mockContactMessages].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  },

  async adminListNewsletter(token: string): Promise<AdminNewsletterSubscriber[]> {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockNewsletterSubscribers();
    return [...mockNewsletterSubscribers].sort(
      (a, b) =>
        new Date(b.subscribedAt).getTime() - new Date(a.subscribedAt).getTime()
    );
  },

  async adminUpdateNewsletter(
    token: string,
    id: string,
    payload: { unsubscribed: boolean }
  ): Promise<AdminNewsletterSubscriber> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockNewsletterSubscribers();
    const index = mockNewsletterSubscribers.findIndex((s) => s.id === id);
    if (index === -1) throw new Error("Subscriber not found");
    const updated: AdminNewsletterSubscriber = {
      ...mockNewsletterSubscribers[index],
      unsubscribedAt: payload.unsubscribed
        ? new Date().toISOString()
        : undefined,
    };
    mockNewsletterSubscribers[index] = updated;
    return updated;
  },

  async getDocumentData(
    donationId: string,
    token: string,
    kind: DocumentKind
  ) {
    await delay(200);
    if (token !== MOCK_DOWNLOAD_TOKEN) {
      throw new Error("Invalid or expired download link.");
    }
    seedMockAdminDonations();
    const record = mockDonations.get(donationId);
    if (!record || record.status !== "paid") {
      throw new Error("Document not available for this donation.");
    }
    const settings = seedMockSettings();
    const listItem = toListItem(donationId, record);
    const source = {
      id: donationId,
      donorName: listItem.donorName,
      donorEmail: listItem.email,
      phone: record.phone,
      amount: listItem.amount,
      category: listItem.category,
      paidAt: listItem.paidAt,
      pan: record.pan,
      address: record.address,
      receiptNumber: record.receiptNumber,
      razorpayPaymentId: record.razorpayPaymentId,
    };
    // Receipt and certificate templates share ReceiptDocumentData; kind is applied in document-preview renderers.
    return buildReceiptDocumentData(source, settings);
  },

  async adminGetSettings(token: string): Promise<AdminSettings> {
    await delay(300);
    assertFinanceOrSuper(token);
    return { ...seedMockSettings() };
  },

  async adminUpdateSettings(
    token: string,
    payload: UpdateAdminSettingsPayload
  ): Promise<AdminSettings> {
    await delay(400);
    assertSuperAdmin(token);
    const current = seedMockSettings();
    mockSettings = { ...current, ...payload };
    return { ...mockSettings };
  },

  async adminResetReceiptSequence(
    token: string,
    payload: ResetReceiptSequencePayload
  ): Promise<AdminSettings> {
    await delay(300);
    assertSuperAdmin(token);
    const current = seedMockSettings();
    mockSettings = {
      ...current,
      financialYear: payload.financialYear,
      lastReceiptNumber: payload.lastNumber ?? 0,
    };
    return { ...mockSettings };
  },

  async adminListUsers(token: string): Promise<AdminUserListItem[]> {
    await delay(300);
    assertSuperAdmin(token);
    seedMockAdminUsers();
    return [...mockAdminUsers].sort(
      (a, b) =>
        new Date(b.createdAt ?? 0).getTime() -
        new Date(a.createdAt ?? 0).getTime()
    );
  },

  async adminCreateUser(
    token: string,
    payload: CreateAdminUserPayload
  ): Promise<AdminUserListItem> {
    await delay(400);
    assertSuperAdmin(token);
    seedMockAdminUsers();
    if (mockAdminUsers.some((u) => u.email === payload.email)) {
      throw new Error("Email already in use");
    }
    const user: AdminUserListItem = {
      id: `admin_${Date.now()}`,
      email: payload.email,
      name: payload.name,
      role: payload.role,
      mfaEnabled: false,
      lastLoginAt: null,
      createdAt: new Date().toISOString(),
    };
    mockAdminUsers.unshift(user);
    void payload.password;
    return user;
  },

  async adminUpdateUser(
    token: string,
    id: string,
    payload: UpdateAdminUserPayload
  ): Promise<AdminUserListItem> {
    await delay(400);
    assertSuperAdmin(token);
    seedMockAdminUsers();
    const index = mockAdminUsers.findIndex((u) => u.id === id);
    if (index === -1) {
      throw new Error("User not found");
    }
    mockAdminUsers[index] = {
      ...mockAdminUsers[index],
      ...payload,
    };
    return { ...mockAdminUsers[index] };
  },

  async adminDeleteUser(token: string, id: string) {
    await delay(400);
    assertSuperAdmin(token);
    seedMockAdminUsers();
    const currentId = mockCurrentAdminUser?.id;
    if (!currentId && typeof window !== "undefined") {
      const session = getAdminSession();
      if (session?.user) {
        mockCurrentAdminUser = session.user;
      }
    }
    const actorId = mockCurrentAdminUser?.id;
    if (actorId && id === actorId) {
      throw new Error("You cannot delete your own account");
    }
    const index = mockAdminUsers.findIndex((u) => u.id === id);
    if (index === -1) {
      throw new Error("User not found");
    }
    const target = mockAdminUsers[index];
    if (
      target.role === "super_admin" &&
      mockAdminUsers.filter((u) => u.role === "super_admin").length <= 1
    ) {
      throw new Error("Cannot delete the last super admin");
    }
    mockAdminUsers.splice(index, 1);
    return { success: true };
  },

  async adminRegenerateReceipt(token: string, id: string) {
    await delay(400);
    assertFinanceOrSuper(token);
    const record = mockDonations.get(id);
    if (record) {
      record.receiptRegeneratedAt = new Date().toISOString();
    }
    return { success: true };
  },

  async adminRegenerateCertificate(token: string, id: string) {
    await delay(400);
    assertFinanceOrSuper(token);
    const record = mockDonations.get(id);
    if (record) {
      record.certificateRegeneratedAt = new Date().toISOString();
    }
    return { success: true };
  },

  async adminResendEmail(token: string, id: string) {
    await delay(400);
    assertFinanceOrSuper(token);
    const record = mockDonations.get(id);
    if (record) {
      record.emailSentAt = new Date().toISOString();
    }
    return { success: true };
  },

  async adminListAuditLog(
    token: string,
    params?: AdminAuditLogListParams
  ): Promise<AdminPaginatedAuditLogResponse> {
    await delay(300);
    assertSuperAdminRole(token);
    seedMockAuditLog();

    const ACTION_GROUPS: Record<string, string[]> = {
      auth: ["admin.login"],
      donations: ["donation.", "donations."],
      content: [
        "contact.",
        "newsletter.",
        "event.",
        "testimonial.",
        "home_content.",
        "about_content.",
        "programs_content.",
        "impact_content.",
        "legal_content.",
        "cms_media.",
      ],
      users: ["admin_user."],
      settings: ["settings."],
    };

    let filtered = [...mockAuditLog];

    if (params?.action) {
      filtered = filtered.filter((e) => e.action === params.action);
    }

    if (params?.actionPrefix) {
      const group = ACTION_GROUPS[params.actionPrefix];
      if (group) {
        filtered = filtered.filter((e) =>
          group.some((token) =>
            token.endsWith(".")
              ? e.action.startsWith(token)
              : e.action === token
          )
        );
      } else {
        filtered = filtered.filter((e) =>
          e.action.startsWith(params.actionPrefix!)
        );
      }
    }

    if (params?.search?.trim()) {
      const q = params.search.trim().toLowerCase();
      filtered = filtered.filter((e) => {
        const haystack = [
          e.adminEmail,
          e.action,
          e.targetType ?? "",
          e.targetId ?? "",
          e.detail ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (params?.dateFrom) {
      const start = new Date(`${params.dateFrom}T00:00:00.000Z`).getTime();
      filtered = filtered.filter(
        (e) => new Date(e.createdAt).getTime() >= start
      );
    }
    if (params?.dateTo) {
      const end = new Date(`${params.dateTo}T23:59:59.999Z`).getTime();
      filtered = filtered.filter((e) => new Date(e.createdAt).getTime() <= end);
    }

    filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    const pageSize = Math.min(Math.max(params?.pageSize ?? 20, 1), 100);
    const total = filtered.length;
    const totalPages = total === 0 ? 1 : Math.ceil(total / pageSize);
    const page = Math.min(Math.max(params?.page ?? 1, 1), totalPages);
    const startIdx = (page - 1) * pageSize;

    return {
      items: filtered.slice(startIdx, startIdx + pageSize),
      total,
      page,
      pageSize,
      totalPages,
    };
  },

  async adminUpdateContact(
    token: string,
    id: string,
    payload: ContactUpdatePayload
  ): Promise<AdminContactMessage> {
    await delay(300);
    assertFinanceOrSuper(token);
    const index = mockContactMessages.findIndex((m) => m.id === id);
    if (index === -1) throw new Error("Contact message not found");
    const current = mockContactMessages[index];
    const updated: AdminContactMessage = {
      ...current,
      ...payload,
      handledAt:
        payload.status === "closed"
          ? new Date().toISOString()
          : current.handledAt ?? null,
    };
    mockContactMessages[index] = updated;
    return updated;
  },

  async adminReconcileDonation(
    token: string,
    id: string,
    payload: DonationReconcilePayload
  ): Promise<AdminDonationDetail> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockAdminDonations();
    const record = mockDonations.get(id);
    if (!record) throw new Error("Donation not found");
    if (record.status !== "pending" && record.status !== "failed") {
      throw new Error("Only pending or failed donations can be reconciled");
    }
    const orderId = (record.razorpayOrderId || "").toLowerCase();
    if (orderId.includes("_paid") || orderId.includes("_captured")) {
      throw new Error(
        "Gateway shows a captured payment. Use mark-paid-from-gateway instead of reconciling."
      );
    }
    record.reconciliationStatus = payload.reconciliationStatus;
    if (payload.adminNotes !== undefined) {
      record.adminNotes = payload.adminNotes.trim() ? payload.adminNotes : null;
    }
    record.reviewedAt = new Date().toISOString();
    return this.adminGetDonation(token, id);
  },

  async adminGetDonationGatewayStatus(
    token: string,
    id: string
  ): Promise<GatewayPaymentStatus> {
    await delay(200);
    assertFinanceOrSuper(token);
    seedMockAdminDonations();
    const record = mockDonations.get(id);
    if (!record) throw new Error("Donation not found");
    if (record.status !== "pending" && record.status !== "failed") {
      throw new Error(
        "Gateway check is only available for pending or failed donations"
      );
    }
    const orderId = record.razorpayOrderId || "";
    if (!orderId) {
      throw new Error("Donation has no payment order id");
    }
    const lower = orderId.toLowerCase();
    if (lower.includes("_failed") || record.status === "failed") {
      return {
        orderId,
        status: "failed",
        paymentId: `pay_mock_${id}`,
        amountPaise: record.amount * 100,
        message: "Mock payment failed at gateway",
      };
    }
    if (
      lower.includes("_paid") ||
      lower.includes("_captured") ||
      record.status === "pending"
    ) {
      return {
        orderId,
        status: "paid",
        paymentId: `pay_mock_${id}`,
        amountPaise: record.amount * 100,
        message: "Mock payment captured at gateway",
      };
    }
    return {
      orderId,
      status: "pending",
      paymentId: null,
      amountPaise: record.amount * 100,
      message: "Mock order has no captured payment yet",
    };
  },

  async adminMarkPaidFromGateway(
    token: string,
    id: string,
    payload?: MarkPaidFromGatewayPayload
  ): Promise<AdminDonationDetail> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockAdminDonations();
    const record = mockDonations.get(id);
    if (!record) throw new Error("Donation not found");
    if (record.status === "paid") {
      const detail = await this.adminGetDonation(token, id);
      return { ...detail, emailSent: Boolean(record.emailSentAt) };
    }
    if (record.status !== "pending" && record.status !== "failed") {
      throw new Error("Only pending or failed donations can be marked paid");
    }
    const gateway = await this.adminGetDonationGatewayStatus(token, id);
    if (gateway.status !== "paid" || !gateway.paymentId) {
      throw new Error(
        gateway.message || "Gateway does not show a captured payment"
      );
    }
    if (payload?.paymentId && payload.paymentId !== gateway.paymentId) {
      throw new Error("Payment id does not match gateway");
    }
    record.status = "paid";
    record.razorpayPaymentId = gateway.paymentId;
    record.paidAt = new Date().toISOString();
    record.emailSentAt = new Date().toISOString();
    record.reconciliationStatus = "reviewed";
    const detail = await this.adminGetDonation(token, id);
    return { ...detail, emailSent: true };
  },

  async adminSendPaymentFollowup(
    token: string,
    id: string
  ): Promise<{ success: boolean }> {
    await delay(200);
    assertFinanceOrSuper(token);
    seedMockAdminDonations();
    const record = mockDonations.get(id);
    if (!record) throw new Error("Donation not found");
    if (record.status !== "pending" && record.status !== "failed") {
      throw new Error("Follow-up email is only for pending or failed donations");
    }
    if (!record.email) throw new Error("Donation has no donor email");
    return { success: true };
  },

  async adminGetFinanceQueueSummary(token: string): Promise<FinanceQueueSummary> {
    await delay(200);
    assertFinanceOrSuper(token);
    seedMockAdminDonations();
    let pendingCount = 0;
    let failedCount = 0;
    let openReconCount = 0;
    let pendingAmountPaise = 0;
    let failedAmountPaise = 0;
    for (const record of mockDonations.values()) {
      if (record.status === "pending") {
        pendingCount += 1;
        pendingAmountPaise += record.amount * 100;
      } else if (record.status === "failed") {
        failedCount += 1;
        failedAmountPaise += record.amount * 100;
      } else {
        continue;
      }
      const recon = record.reconciliationStatus ?? "open";
      if (recon === "open") openReconCount += 1;
    }
    return {
      pendingCount,
      failedCount,
      openReconCount,
      pendingAmountPaise,
      failedAmountPaise,
      totalAmountPaise: pendingAmountPaise + failedAmountPaise,
    };
  },

  async adminListDonors(
    token: string,
    search?: string
  ): Promise<AdminDonorListItem[]> {
    await delay(300);
    assertAdminToken(token);
    seedMockAdminDonations();
    const byEmail = new Map<string, AdminDonorListItem>();
    for (const [id, record] of mockDonations.entries()) {
      if (!record.email) continue;
      const existing = byEmail.get(record.email);
      const paid = record.status === "paid" ? record.amount : 0;
      const name =
        record.donorFirstName && record.donorLastName
          ? `${record.donorFirstName} ${record.donorLastName}`
          : record.email;
      if (!existing) {
        byEmail.set(record.email, {
          email: record.email,
          name,
          donationCount: 1,
          totalAmount: paid,
          lastDonationAt: record.createdAt,
        });
      } else {
        existing.donationCount += 1;
        existing.totalAmount += paid;
        if (
          new Date(record.createdAt).getTime() >
          new Date(existing.lastDonationAt ?? 0).getTime()
        ) {
          existing.lastDonationAt = record.createdAt;
        }
      }
      void id;
    }
    let donors = [...byEmail.values()].sort(
      (a, b) =>
        new Date(b.lastDonationAt ?? 0).getTime() -
        new Date(a.lastDonationAt ?? 0).getTime()
    );
    if (search?.trim()) {
      const term = search.trim().toLowerCase();
      donors = donors.filter(
        (d) =>
          d.email.toLowerCase().includes(term) ||
          d.name.toLowerCase().includes(term)
      );
    }
    return donors;
  },

  async adminGetDonor(token: string, email: string): Promise<AdminDonorDetail> {
    await delay(300);
    assertAdminToken(token);
    seedMockAdminDonations();
    const donations = [...mockDonations.entries()]
      .filter(([, record]) => record.email === email)
      .map(([id, record]) => ({
        id,
        amount: record.amount,
        status: record.status,
        category: record.category,
        createdAt: record.createdAt,
        receiptNumber: record.receiptNumber ?? null,
      }));
    if (donations.length === 0) {
      throw new Error("Donor not found");
    }
    const first = mockDonations.get(donations[0].id)!;
    return {
      email,
      name:
        first.donorFirstName && first.donorLastName
          ? `${first.donorFirstName} ${first.donorLastName}`
          : email,
      phone: first.phone ?? null,
      donationCount: donations.length,
      totalAmount: donations
        .filter((d) => d.status === "paid")
        .reduce((sum, d) => sum + d.amount, 0),
      donations,
    };
  },

  async adminListEvents(token: string): Promise<AdminEvent[]> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockEvents();
    const events = mockEvents();
    return [...events].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  },

  async adminCreateEvent(
    token: string,
    payload: CreateAdminEventPayload
  ): Promise<AdminEvent> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockEvents();
    const events = mockEvents();
    const event: AdminEvent = {
      id: `evt_${Date.now()}`,
      title: payload.title,
      eventDate: payload.eventDate,
      date: formatEventDateFromIso(payload.eventDate),
      description: payload.description,
      status: payload.status,
      items: payload.items ?? null,
      images: payload.images ?? [],
      heroImage: payload.heroImage ?? null,
      featured: payload.featured ?? false,
      published: payload.published ?? true,
      publishAt: payload.publishAt ?? null,
      sortOrder: payload.sortOrder ?? 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    events.push(event);
    return event;
  },

  async adminUpdateEvent(
    token: string,
    id: string,
    payload: UpdateAdminEventPayload
  ): Promise<AdminEvent> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockEvents();
    const events = mockEvents();
    const index = events.findIndex((e) => e.id === id);
    if (index === -1) throw new Error("Event not found");
    const current = events[index];
    events[index] = {
      ...current,
      title: payload.title ?? current.title,
      eventDate: payload.eventDate ?? current.eventDate,
      date: payload.eventDate
        ? formatEventDateFromIso(payload.eventDate)
        : current.date,
      description: payload.description ?? current.description,
      status: payload.status ?? current.status,
      items: payload.items ?? current.items,
      images: payload.images ?? current.images,
      heroImage: payload.heroImage ?? current.heroImage,
      featured: payload.featured ?? current.featured,
      published: payload.published ?? current.published,
      publishAt:
        payload.publishAt !== undefined ? payload.publishAt : current.publishAt,
      sortOrder: payload.sortOrder ?? current.sortOrder,
      updatedAt: new Date().toISOString(),
    };
    return events[index];
  },

  async adminDeleteEvent(token: string, id: string) {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockEvents();
    const events = mockEvents();
    const index = events.findIndex((e) => e.id === id);
    if (index === -1) throw new Error("Event not found");
    events.splice(index, 1);
    return { success: true };
  },

  async adminUploadEventImage(
    token: string,
    eventId: string,
    file: File,
    slot: "card" | "hero" = "card"
  ) {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockEvents();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const events = mockEvents();
    const index = events.findIndex((e) => e.id === eventId);
    if (index === -1) throw new Error("Event not found");
    const url = await readFileAsDataUrl(file);
    const current = events[index];
    if (slot === "hero") {
      events[index] = {
        ...current,
        heroImage: url,
        updatedAt: new Date().toISOString(),
      };
    } else {
      const images = [...(current.images ?? [])];
      if (!images.includes(url)) images.unshift(url);
      events[index] = {
        ...current,
        images,
        updatedAt: new Date().toISOString(),
      };
    }
    const updated = events[index];
    return {
      url,
      images: updated.images ?? [],
      heroImage: updated.heroImage ?? null,
    };
  },

  async adminGetEventNotifyPreview(token: string, eventId: string) {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockEvents();
    const events = mockEvents();
    const event = events.find((e) => e.id === eventId);
    if (!event) throw new Error("Event not found");
    const audience = collectEventNotifyAudience();
    return {
      ...audience,
      announcementSentAt: event.announcementSentAt ?? null,
    };
  },

  async adminSendEventAnnouncement(token: string, eventId: string) {
    await delay(800);
    assertFinanceOrSuper(token);
    seedMockEvents();
    const events = mockEvents();
    const index = events.findIndex((e) => e.id === eventId);
    if (index === -1) throw new Error("Event not found");
    const event = events[index];
    if (event.published === false) {
      throw new Error("Publish the event before sending an announcement");
    }
    if (event.announcementSentAt) {
      throw new Error("Announcement already sent for this event");
    }
    const audience = collectEventNotifyAudience();
    events[index] = {
      ...event,
      announcementSentAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return {
      success: true,
      sent: audience.totalDeduped,
      skipped: 0,
    };
  },

  async getTestimonials(): Promise<Testimonial[]> {
    await delay(300);
    seedMockTestimonials();
    return withTestimonialFallbacks(
      sortTestimonialsForDisplay(
        getMockTestimonialsArray().filter((t) => isTestimonialPublished(t))
      ).map(adminTestimonialToPublic)
    );
  },

  async getHomeContent(): Promise<HomeContent> {
    await delay(300);
    seedMockHomeContent();
    const { updatedAt: _, ...content } = getMockHomeContentStore();
    return mergeHomeContent(content);
  },

  async getAboutContent(): Promise<AboutContent> {
    await delay(300);
    seedMockAboutContent();
    const { updatedAt: _, ...content } = getMockAboutContentStore();
    return mergeAboutContent(content);
  },

  async getLegalContent(): Promise<LegalContent> {
    await delay(300);
    seedMockLegalContent();
    const { updatedAt: _, ...content } = getMockLegalContentStore();
    return mergeLegalContent(content);
  },

  async adminListTestimonials(token: string): Promise<AdminTestimonial[]> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockTestimonials();
    return sortTestimonialsForDisplay(getMockTestimonialsArray());
  },

  async adminCreateTestimonial(
    token: string,
    payload: CreateAdminTestimonialPayload
  ): Promise<AdminTestimonial> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockTestimonials();
    const items = getMockTestimonialsArray();
    if (payload.sortOrder === undefined) {
      bumpTestimonialSortOrders(items);
    }
    const item: AdminTestimonial = {
      id: `tst_${Date.now()}`,
      quote: payload.quote,
      name: payload.name,
      role: payload.role,
      image: payload.image ?? assets.contact[0],
      published: payload.published ?? true,
      publishAt: payload.publishAt ?? null,
      sortOrder: payload.sortOrder ?? 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    items.push(item);
    return item;
  },

  async adminUpdateTestimonial(
    token: string,
    id: string,
    payload: UpdateAdminTestimonialPayload
  ): Promise<AdminTestimonial> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockTestimonials();
    const items = getMockTestimonialsArray();
    const index = items.findIndex((t) => t.id === id);
    if (index === -1) throw new Error("Testimonial not found");
    const current = items[index];
    items[index] = {
      ...current,
      quote: payload.quote ?? current.quote,
      name: payload.name ?? current.name,
      role: payload.role ?? current.role,
      image: payload.image ?? current.image,
      published: payload.published ?? current.published,
      publishAt:
        payload.publishAt !== undefined ? payload.publishAt : current.publishAt,
      sortOrder: payload.sortOrder ?? current.sortOrder,
      updatedAt: new Date().toISOString(),
    };
    return items[index];
  },

  async adminDeleteTestimonial(token: string, id: string) {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockTestimonials();
    const items = getMockTestimonialsArray();
    const index = items.findIndex((t) => t.id === id);
    if (index === -1) throw new Error("Testimonial not found");
    items.splice(index, 1);
    return { success: true };
  },

  async adminUploadTestimonialImage(
    token: string,
    testimonialId: string,
    file: File
  ) {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockTestimonials();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const items = getMockTestimonialsArray();
    const index = items.findIndex((t) => t.id === testimonialId);
    if (index === -1) throw new Error("Testimonial not found");
    const url = await readFileAsDataUrl(file);
    items[index] = {
      ...items[index],
      image: url,
      updatedAt: new Date().toISOString(),
    };
    return { url, image: url };
  },

  async adminGetHomeContent(token: string): Promise<AdminHomeContent> {
    await delay(300);
    assertCmsWriter(token);
    seedMockHomeContent();
    const store = getMockHomeContentStore();
    const body = {
      hero: store.hero,
      eventsSection: store.eventsSection,
      testimonialsSection: store.testimonialsSection,
      impact: store.impact,
      aboutStory: store.aboutStory,
      donateSection: store.donateSection,
      featuredPrograms: store.featuredPrograms,
      miniGallery: store.miniGallery,
    };
    return {
      ...body,
      draft: body,
      updatedAt: store.updatedAt ?? null,
    };
  },

  async adminUpdateHomeContent(
    token: string,
    payload: UpdateAdminHomeContentPayload
  ): Promise<AdminHomeContent> {
    await delay(300);
    assertCmsWriter(token);
    seedMockHomeContent();
    const store = getMockHomeContentStore();
    if (payload.hero) {
      store.hero = { ...store.hero, ...payload.hero };
      if (payload.hero.headingLines) {
        store.hero.headingLines = payload.hero.headingLines;
      }
    }
    if (payload.eventsSection) {
      store.eventsSection = { ...store.eventsSection, ...payload.eventsSection };
    }
    if (payload.testimonialsSection) {
      store.testimonialsSection = {
        ...store.testimonialsSection,
        ...payload.testimonialsSection,
      };
    }
    if (payload.impact) {
      store.impact = {
        ...store.impact,
        ...payload.impact,
        metrics: payload.impact.metrics ?? store.impact.metrics,
      };
    }
    if (payload.aboutStory) {
      store.aboutStory = {
        ...store.aboutStory,
        ...payload.aboutStory,
        highlightKeywords:
          payload.aboutStory.highlightKeywords ?? store.aboutStory.highlightKeywords,
        timeline: payload.aboutStory.timeline ?? store.aboutStory.timeline,
      };
    }
    if (payload.donateSection) {
      store.donateSection = {
        ...store.donateSection,
        ...payload.donateSection,
      };
    }
    if (payload.featuredPrograms) {
      store.featuredPrograms = {
        ...store.featuredPrograms,
        ...payload.featuredPrograms,
        slugs: payload.featuredPrograms.slugs ?? store.featuredPrograms.slugs,
      };
    }
    if (payload.miniGallery) {
      store.miniGallery = {
        ...store.miniGallery,
        ...payload.miniGallery,
        images: payload.miniGallery.images ?? store.miniGallery.images,
      };
    }
    store.updatedAt = new Date().toISOString();
    const body = {
      hero: store.hero,
      eventsSection: store.eventsSection,
      testimonialsSection: store.testimonialsSection,
      impact: store.impact,
      aboutStory: store.aboutStory,
      donateSection: store.donateSection,
      featuredPrograms: store.featuredPrograms,
      miniGallery: store.miniGallery,
    };
    return {
      ...body,
      draft: body,
      updatedAt: store.updatedAt,
    };
  },

  async adminGetLegalContent(token: string): Promise<AdminLegalContent> {
    await delay(300);
    assertCmsWriter(token);
    seedMockLegalContent();
    const store = getMockLegalContentStore();
    const { updatedAt, ...content } = store;
    return { ...content, draft: content, updatedAt: updatedAt ?? null };
  },

  async adminUpdateLegalContent(
    token: string,
    payload: UpdateAdminLegalContentPayload
  ): Promise<AdminLegalContent> {
    await delay(300);
    assertSuperAdmin(token);
    seedMockLegalContent();
    const store = getMockLegalContentStore();

    if (payload.privacyPolicy) {
      store.privacyPolicy = {
        ...store.privacyPolicy,
        ...payload.privacyPolicy,
        sections: payload.privacyPolicy.sections ?? store.privacyPolicy.sections,
      };
    }
    if (payload.termsOfService) {
      store.termsOfService = {
        ...store.termsOfService,
        ...payload.termsOfService,
        sections: payload.termsOfService.sections ?? store.termsOfService.sections,
      };
    }
    store.updatedAt = new Date().toISOString();
    return getMockLegalContentStore();
  },

  async adminUploadHomeHeroImage(token: string, file: File) {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockHomeContent();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const store = getMockHomeContentStore();
    store.hero = { ...store.hero, heroImage: url };
    store.updatedAt = new Date().toISOString();
    return { url, heroImage: url };
  },

  async adminUploadHomeAboutImage(token: string, file: File) {
    await delay(400);
    assertCmsWriter(token);
    seedMockHomeContent();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const store = getMockHomeContentStore();
    store.aboutStory = { ...store.aboutStory, missionImage: url };
    store.updatedAt = new Date().toISOString();
    return { url, missionImage: url };
  },

  async adminGetAboutContent(token: string): Promise<AdminAboutContent> {
    await delay(300);
    assertCmsWriter(token);
    seedMockAboutContent();
    const store = getMockAboutContentStore();
    const { updatedAt, ...content } = store;
    return { ...content, draft: content, updatedAt: updatedAt ?? null };
  },

  async adminUpdateAboutContent(
    token: string,
    payload: UpdateAdminAboutContentPayload
  ): Promise<AdminAboutContent> {
    await delay(300);
    assertFinanceOrSuper(token);
    seedMockAboutContent();
    const store = getMockAboutContentStore();
    if (payload.hero) store.hero = { ...store.hero, ...payload.hero };
    if (payload.intro) {
      store.intro = {
        ...store.intro,
        ...payload.intro,
        paragraphs: payload.intro.paragraphs ?? store.intro.paragraphs,
        founderCard: {
          ...store.intro.founderCard,
          ...(payload.intro.founderCard ?? {}),
          template: normalizeFounderCardTemplate(
            payload.intro.founderCard?.template ??
              store.intro.founderCard.template
          ),
        },
      };
    }
    if (payload.purpose) {
      store.purpose = {
        vision: { ...store.purpose.vision, ...(payload.purpose.vision ?? {}) },
        mission: { ...store.purpose.mission, ...(payload.purpose.mission ?? {}) },
      };
    }
    if (payload.story) store.story = { ...store.story, ...payload.story };
    if (payload.values) {
      store.values = {
        ...store.values,
        ...payload.values,
        items: (payload.values.items ?? store.values.items).map((item) => ({
          ...item,
          icon: normalizeAboutValueIcon(item.icon),
        })),
      };
    }
    if (payload.closingCta) {
      store.closingCta = { ...store.closingCta, ...payload.closingCta };
    }
    if (payload.gallery) store.gallery = { ...store.gallery, ...payload.gallery };
    if (payload.leadership) {
      store.leadership = { ...store.leadership, ...payload.leadership };
    }
    store.updatedAt = new Date().toISOString();
    const { updatedAt, ...content } = store;
    return { ...content, updatedAt: updatedAt ?? null };
  },

  async adminUploadAboutHeroImage(token: string, file: File) {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockAboutContent();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const store = getMockAboutContentStore();
    store.hero = { ...store.hero, imageSrc: url };
    store.updatedAt = new Date().toISOString();
    return { url, imageSrc: url };
  },

  async adminUploadAboutFounderImage(token: string, file: File) {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockAboutContent();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const store = getMockAboutContentStore();
    store.intro = {
      ...store.intro,
      founderCard: {
        ...store.intro.founderCard,
        imageSrc: url,
        template: normalizeFounderCardTemplate(store.intro.founderCard.template),
      },
    };
    store.updatedAt = new Date().toISOString();
    return { url, imageSrc: url };
  },

  async adminUploadAboutGalleryImage(token: string, file: File, alt = "") {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockAboutContent();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const store = getMockAboutContentStore();
    const sortOrder =
      Math.max(-1, ...store.gallery.images.map((i) => i.sortOrder)) + 1;
    const image = {
      id: `gallery_${Date.now()}`,
      src: url,
      alt: alt || "Community field work",
      sortOrder,
    };
    store.gallery = {
      ...store.gallery,
      images: [...store.gallery.images, image],
    };
    store.updatedAt = new Date().toISOString();
    return { url, image };
  },

  async adminUploadAboutLeadershipImage(
    token: string,
    file: File,
    memberId?: string | null
  ) {
    await delay(400);
    assertFinanceOrSuper(token);
    seedMockAboutContent();
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const store = getMockAboutContentStore();
    if (memberId) {
      store.leadership = {
        ...store.leadership,
        members: store.leadership.members.map((m) =>
          m.id === memberId ? { ...m, image: url } : m
        ),
      };
      store.updatedAt = new Date().toISOString();
    }
    return { url, image: url, memberId: memberId ?? null };
  },

  async adminResetAboutContentToSeed(token: string) {
    await delay(200);
    assertFinanceOrSuper(token);
    resetMockAboutContentToSeed();
    const store = getMockAboutContentStore();
    const { updatedAt, ...content } = store;
    return { ...content, updatedAt: updatedAt ?? null };
  },

  async adminResetTestimonialsToSeed(token: string) {
    await delay(200);
    assertFinanceOrSuper(token);
    seedMockTestimonials();
    resetMockTestimonialsToSeed();
    return { success: true, count: getMockTestimonialsArray().length };
  },

  async adminResetHomeContentToSeed(token: string) {
    await delay(200);
    assertCmsWriter(token);
    resetMockHomeContentToSeed();
    const store = getMockHomeContentStore();
    return {
      success: true,
      hero: store.hero,
      eventsSection: store.eventsSection,
      testimonialsSection: store.testimonialsSection,
    };
  },

  async getProgramsContent(): Promise<ProgramsContent> {
    await delay(200);
    return mergeProgramsContent(mockProgramsContent);
  },

  async getImpactContent(): Promise<ImpactContent> {
    await delay(200);
    return mergeImpactContent(mockImpactContent);
  },

  async adminPublishHomeContent(token: string): Promise<AdminHomeContent> {
    await delay(200);
    assertCmsWriter(token);
    seedMockHomeContent();
    pushMockRevision("home", "singleton");
    return this.adminGetHomeContent(token);
  },

  async adminPublishAboutContent(token: string): Promise<AdminAboutContent> {
    await delay(200);
    assertCmsWriter(token);
    seedMockAboutContent();
    pushMockRevision("about", "singleton");
    return this.adminGetAboutContent(token);
  },

  async adminPublishLegalContent(token: string): Promise<AdminLegalContent> {
    await delay(200);
    assertSuperAdmin(token);
    seedMockLegalContent();
    pushMockRevision("legal", "singleton");
    return this.adminGetLegalContent(token);
  },

  async adminPublishProgramsContent(
    token: string
  ): Promise<AdminProgramsContent> {
    await delay(200);
    assertCmsWriter(token);
    pushMockRevision("programs", "singleton");
    return this.adminGetProgramsContent(token);
  },

  async adminPublishImpactContent(token: string): Promise<AdminImpactContent> {
    await delay(200);
    assertCmsWriter(token);
    pushMockRevision("impact", "singleton");
    return this.adminGetImpactContent(token);
  },

  async adminGetHomeRevisions(token: string): Promise<CmsRevision[]> {
    await delay(150);
    assertCmsWriter(token);
    return listMockRevisions("home", "singleton");
  },

  async adminRestoreHomeRevision(
    token: string,
    _id: string
  ): Promise<AdminHomeContent> {
    await delay(200);
    assertCmsWriter(token);
    return this.adminGetHomeContent(token);
  },

  async adminGetAboutRevisions(token: string): Promise<CmsRevision[]> {
    await delay(150);
    assertCmsWriter(token);
    return listMockRevisions("about", "singleton");
  },

  async adminRestoreAboutRevision(
    token: string,
    _id: string
  ): Promise<AdminAboutContent> {
    await delay(200);
    assertCmsWriter(token);
    return this.adminGetAboutContent(token);
  },

  async adminGetLegalRevisions(token: string): Promise<CmsRevision[]> {
    await delay(150);
    assertCmsWriter(token);
    return listMockRevisions("legal", "singleton");
  },

  async adminRestoreLegalRevision(
    token: string,
    _id: string
  ): Promise<AdminLegalContent> {
    await delay(200);
    assertSuperAdmin(token);
    return this.adminGetLegalContent(token);
  },

  async adminGetProgramsRevisions(token: string): Promise<CmsRevision[]> {
    await delay(150);
    assertCmsWriter(token);
    return listMockRevisions("programs", "singleton");
  },

  async adminRestoreProgramsRevision(
    token: string,
    _id: string
  ): Promise<AdminProgramsContent> {
    await delay(200);
    assertCmsWriter(token);
    return this.adminGetProgramsContent(token);
  },

  async adminGetImpactRevisions(token: string): Promise<CmsRevision[]> {
    await delay(150);
    assertCmsWriter(token);
    return listMockRevisions("impact", "singleton");
  },

  async adminRestoreImpactRevision(
    token: string,
    _id: string
  ): Promise<AdminImpactContent> {
    await delay(200);
    assertCmsWriter(token);
    return this.adminGetImpactContent(token);
  },

  async adminGetProgramsContent(token: string): Promise<AdminProgramsContent> {
    await delay(200);
    assertCmsWriter(token);
    const programs = mergeProgramsContent(mockProgramsContent).programs;
    return {
      programs,
      draft: { programs },
      updatedAt: new Date().toISOString(),
    };
  },

  async adminUpdateProgramsContent(
    token: string,
    payload: UpdateAdminProgramsContentPayload
  ): Promise<AdminProgramsContent> {
    await delay(200);
    assertCmsWriter(token);
    if (payload.programs) {
      mockProgramsContent = { programs: payload.programs };
    }
    return this.adminGetProgramsContent(token);
  },

  async adminGetImpactContent(token: string): Promise<AdminImpactContent> {
    await delay(200);
    assertCmsWriter(token);
    const merged = mergeImpactContent(mockImpactContent);
    return {
      ...merged,
      draft: merged,
      updatedAt: new Date().toISOString(),
    };
  },

  async adminUpdateImpactContent(
    token: string,
    payload: UpdateAdminImpactContentPayload
  ): Promise<AdminImpactContent> {
    await delay(200);
    assertCmsWriter(token);
    mockImpactContent = mergeImpactContent({
      hero: payload.hero ?? mockImpactContent.hero,
      categories: payload.categories ?? mockImpactContent.categories,
      gallery: payload.gallery ?? mockImpactContent.gallery,
    });
    return this.adminGetImpactContent(token);
  },

  async adminListCmsMedia(token: string): Promise<CmsMediaItem[]> {
    await delay(150);
    assertCmsWriter(token);
    return mockCmsMedia
      .filter((item) => !item.deletedAt)
      .sort((a, b) => Number(Boolean(b.protected)) - Number(Boolean(a.protected)));
  },

  async adminUploadCmsMedia(
    token: string,
    file: File,
    alt = ""
  ): Promise<CmsMediaItem> {
    await delay(300);
    assertCmsWriter(token);
    if (!ALLOWED_EVENT_IMAGE_TYPES.has(file.type)) {
      throw new Error("Only JPEG, PNG, and WebP images are allowed");
    }
    if (file.size > MAX_EVENT_IMAGE_BYTES) {
      throw new Error("Image must be 5MB or smaller");
    }
    const url = await readFileAsDataUrl(file);
    const item: CmsMediaItem = {
      id: `media_${Date.now()}`,
      url,
      storageKey: `mock/${file.name}`,
      contentType: file.type,
      alt: alt || file.name,
      byteSize: file.size,
      protected: false,
      originKey: null,
      deletedAt: null,
      createdByAdminId: mockCurrentAdminUser?.id ?? null,
      createdAt: new Date().toISOString(),
    };
    mockCmsMedia.unshift(item);
    return item;
  },

  async adminUpdateCmsMedia(
    token: string,
    id: string,
    payload: UpdateCmsMediaPayload
  ): Promise<CmsMediaItem> {
    await delay(150);
    assertCmsWriter(token);
    const index = mockCmsMedia.findIndex(
      (item) => item.id === id && !item.deletedAt
    );
    if (index === -1) throw new Error("Media not found");
    mockCmsMedia[index] = { ...mockCmsMedia[index], alt: payload.alt };
    return mockCmsMedia[index];
  },

  async adminDeleteCmsMedia(
    token: string,
    id: string
  ): Promise<{ success: boolean }> {
    await delay(150);
    assertCmsWriter(token);
    const index = mockCmsMedia.findIndex(
      (item) => item.id === id && !item.deletedAt
    );
    if (index === -1) throw new Error("Media not found");
    if (mockCmsMedia[index].protected) {
      throw new Error("Default media cannot be deleted");
    }
    mockCmsMedia[index] = {
      ...mockCmsMedia[index],
      deletedAt: new Date().toISOString(),
    };
    return { success: true };
  },

  async adminSeedCmsMediaDefaults(token: string): Promise<{
    created: number;
    skipped: number;
    total: number;
  }> {
    await delay(200);
    assertCmsWriter(token);
    const seeds = [
      {
        originKey: "home-hero-charminar",
        alt: "Charminar at dusk — Hyderabad landmark",
      },
      { originKey: "gallery-01", alt: "Community field work" },
      { originKey: "program-healthcare", alt: "Healthcare program" },
    ];
    let created = 0;
    let skipped = 0;
    for (const seed of seeds) {
      if (mockCmsMedia.some((m) => m.originKey === seed.originKey)) {
        skipped += 1;
        continue;
      }
      mockCmsMedia.unshift({
        id: `media_seed_${seed.originKey}`,
        url: `data:image/png;base64,seed-${seed.originKey}`,
        storageKey: `cms/media/seed/${seed.originKey}.webp`,
        contentType: "image/webp",
        alt: seed.alt,
        byteSize: 100,
        protected: true,
        originKey: seed.originKey,
        deletedAt: null,
        createdByAdminId: mockCurrentAdminUser?.id ?? null,
        createdAt: new Date().toISOString(),
      });
      created += 1;
    }
    return { created, skipped, total: created + skipped };
  },
};
